import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partSuppliersApi } from './service';
import type {
  CreatePartSupplierPayload,
  UpdatePartSupplierPayload,
} from './types';

export const partSuppliersKeys = {
  all: ['part-suppliers'] as const,
  forPart: (partId: string) =>
    [...partSuppliersKeys.all, 'for-part', partId] as const,
};

export function usePartSuppliers(partId: string | undefined) {
  return useQuery({
    queryKey: partId ? partSuppliersKeys.forPart(partId) : partSuppliersKeys.all,
    queryFn: () => partSuppliersApi.list(partId as string),
    enabled: Boolean(partId),
  });
}

export function useCreatePartSupplier(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartSupplierPayload) =>
      partSuppliersApi.create(partId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partSuppliersKeys.forPart(partId) });
    },
  });
}

export function useUpdatePartSupplier(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      partSupplierId: string;
      patch: UpdatePartSupplierPayload;
    }) => partSuppliersApi.update(partId, input.partSupplierId, input.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partSuppliersKeys.forPart(partId) });
    },
  });
}

export function useDeletePartSupplier(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partSupplierId: string) =>
      partSuppliersApi.remove(partId, partSupplierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: partSuppliersKeys.forPart(partId) });
    },
  });
}
